import { keepPreviousData, skipToken, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { TeamsApi } from '@/lib/api/teams/team';
import type {
  ResourceFilterStatus,
  ResourceStatus,
  SearchMembershipsSearchParams,
  TeamWithIncludedResponse,
} from '@/lib/api/types/teams';
import { teamsQueryKeys } from '@/lib/query-keys/teams';

export const getTeamQueryOptions = ({
  teamId,
  resourceStatus,
}: {
  teamId: number;
  resourceStatus?: ResourceFilterStatus;
}) => {
  return {
    queryKey: teamsQueryKeys.team({ teamId, resourceStatus }),
    queryFn: () => TeamsApi.getTeam(teamId, resourceStatus),
    enabled: !!teamId,
    staleTime: 60 * 60 * 1000,
  };
};

export const useGetTeamQuery = ({
  teamId,
  resourceStatus,
  options = {},
}: {
  teamId?: number | null;
  resourceStatus?: ResourceStatus;
  options?: Partial<UseQueryOptions<TeamWithIncludedResponse, Error, TeamWithIncludedResponse>>;
}) => {
  return useQuery({
    queryKey: teamsQueryKeys.team({ teamId, resourceStatus }),
    queryFn: teamId ? () => TeamsApi.getTeam(teamId, resourceStatus) : skipToken,
    ...options,
  });
};

export const getSearchMembershipsByTeamQueryOptions = ({
  teamId,
  searchParams,
}: {
  teamId: number;
  searchParams?: SearchMembershipsSearchParams;
}) => {
  return {
    queryKey: teamsQueryKeys.searchTeamMembers({ teamId, searchParams }),
    queryFn: () => TeamsApi.searchTeamMembershipsByTeam(teamId, searchParams),
    enabled: !!teamId,
  };
};

export const useSearchMembershipsByTeamQuery = ({
  teamId,
  searchParams,
}: {
  teamId: number;
  searchParams?: SearchMembershipsSearchParams;
}) => {
  return useQuery<TeamWithIncludedResponse>({
    ...getSearchMembershipsByTeamQueryOptions({
      teamId,
      searchParams,
    }),
    placeholderData: keepPreviousData,
  });
};
